import type { Attribution } from './attribution'

/**
 * Tab-scoped buffer for the captured attribution (FE-320).
 *
 * sessionStorage, not cookies or localStorage: it has to survive the OAuth
 * redirect of a Google / Apple login (same tab) and nothing more. Every
 * accessor is guarded — private windows and blocked storage must not break
 * the app.
 */
export const ATTRIBUTION_STORAGE_KEY = 'xyro:attribution'

const getStorage = (): Storage | null => {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

export const readStoredAttribution = (): Attribution | null => {
  const raw = getStorage()?.getItem(ATTRIBUTION_STORAGE_KEY)
  if (!raw) return null

  try {
    const parsed: unknown = JSON.parse(raw)

    return isAttribution(parsed) ? parsed : null
  } catch {
    return null
  }
}

export const storeAttribution = (attribution: Attribution) => {
  try {
    getStorage()?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution))
  } catch {
    // NOTE: storage quota or privacy mode; the capture is simply not buffered
  }
}

export const clearStoredAttribution = () => {
  try {
    getStorage()?.removeItem(ATTRIBUTION_STORAGE_KEY)
  } catch {
    // NOTE: nothing to clear when storage is unavailable
  }
}

const isAttribution = (value: unknown): value is Attribution =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as Attribution).landingUrl === 'string' &&
  typeof (value as Attribution).capturedAt === 'string'
